import { SiteUrls } from '@tableau/shared-urls';
import { Promise } from 'es6-promise';
import * as React from 'react';

import { Messages } from '../../codegen/Localize';
import { CommonStyles } from '../../common/react/CommonStyles';
import { FilteringListBox } from '../../common/react/components/FilteringListBox';
import { SparseCachedList } from '../../common/react/SparseCachedList';
import { IDataAlertInfo } from '../models/IDataAlertInfo';
import { Module } from '../Module';
import { DataAlertUsers } from '../resources/DataAlertUsers';
import { IUser } from '../ServerApi/types/IUser';
import { ModalService } from './ModalService';
import { ServerService } from './ServerService';

interface DataAlertRecipientsDialogScope extends ng.IScope {
  filteringListBoxProps: FilteringListBox.Props<IUser>;
}

export class ManageDataAlertRecipientsDialog {
  // Search string filter for filtering recipients in the list box
  private filter: string;

  // Cached list of items fetched from the user query provided to the component
  private itemCache: SparseCachedList<FilteringListBox.Item<IUser>, IUser>;

  // Item store for the FilteredListBox component (fetches items from the item cache)
  private itemStore = { getItems: this.getItems.bind(this) };

  private dataAlert: IDataAlertInfo;

  public static $inject = [
    'DataAlertUsers',
    'ModalService',
    'ServerService',
  ];

  constructor(
    private DataAlertUsers: DataAlertUsers,
    private ModalService: ModalService,
    private ServerService: ServerService,
  ) {
  }

  public open(scope: ng.IScope, dataAlert: IDataAlertInfo) {
    this.dataAlert = dataAlert;
    this.createCacheWithFilter('');

    const recipientsDialogScope = scope.$new() as DataAlertRecipientsDialogScope;
    recipientsDialogScope.filteringListBoxProps = {
      searchBoxPlaceholder: Messages.searchRecipients(),
      matchPrefixesOnly: false,
      itemStore: this.itemStore,
      style: ManageDataAlertRecipientsDialog.STYLE,
      listBoxStyle: ManageDataAlertRecipientsDialog.LIST_BOX_STYLE,
    };

    const modalInstance = this.ModalService.open({
      templateUrl: 'dataAlertRecipientsDialog.html',
      scope: recipientsDialogScope
    });
    modalInstance.result.finally(() => recipientsDialogScope.$destroy());
  }

  private getItems(filter: string, start: number, end: number): Promise<FilteringListBox.Item<IUser>[]> {
    if (filter !== this.filter) {
      this.createCacheWithFilter(filter);
    }

    return this.itemCache.getItems(start, end);
  }

  private createCacheWithFilter(filter: string) {
    const query = this.DataAlertUsers.getUsersQuery(this.dataAlert.id, filter);
    this.filter = filter;
    this.itemCache = new SparseCachedList<FilteringListBox.Item<IUser>, IUser>(query, (user: IUser) => {
      const siteUrls = new SiteUrls({siteUrlName: this.ServerService.sessionSiteUrlName(), queryString: window.location.search});
      return {
        label: user.displayName,
        url: siteUrls.userContentUrl(user.username, user.domainName),
        rawItem: user,
      };
    });
  }
}

export namespace ManageDataAlertRecipientsDialog {

  export const STYLE: React.CSSProperties = {
    position: 'relative',
    height: '300px'
  };

  export const LIST_BOX_STYLE: React.CSSProperties = {
    borderWidth: CommonStyles.Sizes.TextBoxBorderSize,
    borderColor: CommonStyles.Colors.BorderColor,
    borderStyle: 'solid'
  };
}

Module.instance().service('ManageDataAlertRecipientsDialog', ManageDataAlertRecipientsDialog);
